import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Envelope, Paperclip, CheckCircle, DownloadSimple,
  ArrowsClockwise, ArrowUUpLeft, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";

const fmtTime = (iso) => {
  if (!iso) return "";
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return new Date(iso).toLocaleString("en-CA", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

/**
 * Accounts Receivable Queue — a filtered view of the single platform Task
 * Engine (task_type = "Accounts Receivable"). Preserves the management
 * oversight of the legacy AR widget but reads / writes through /api/tasks so
 * we don't maintain a parallel workflow system.
 */
export default function AccountsReceivableQueueWidget() {
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        scope: "team",
        task_type: "Accounts Receivable",
      };
      if (showDone) {
        params.status = "Completed";
      } else {
        params.status_ne = "Completed,Cancelled";
      }
      const res = await api.get("/tasks", { params });
      setTasks(res.data);
    } catch (e) {
      // 403 means the user isn't Office Staff / Admin / Owner — just hide.
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDone]);

  const setStatus = async (task, nextStatus, successMsg) => {
    setWorking((w) => ({ ...w, [task.id]: true }));
    try {
      await api.patch(`/tasks/${task.id}`, { status: nextStatus });
      toast.success(successMsg);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update task");
    } finally {
      setWorking((w) => ({ ...w, [task.id]: false }));
    }
  };

  return (
    <div
      data-testid="ar-queue-widget"
      className="border border-divider bg-surface"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-divider px-5 py-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-charcoal">
            <Envelope size={16} weight="regular" /> Accounts Receivable Queue
          </h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            Tasks of type <span className="font-medium">Accounts Receivable</span> — enter into WTBS, then mark complete.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="ar-queue-open-tasks-page"
            onClick={() => nav("/tasks?tab=team&task_type=Accounts%20Receivable")}
            className="inline-flex items-center gap-1 rounded-md border border-divider-strong px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-alt"
          >
            <ArrowSquareOut size={12} weight="bold" /> Open in Tasks
          </button>
          <div className="flex rounded-md border border-divider-strong text-xs">
            <button
              data-testid="ar-queue-filter-open"
              onClick={() => setShowDone(false)}
              className={`px-2.5 py-1 font-semibold ${!showDone ? "bg-charcoal text-ink-inverse" : "text-ink-secondary hover:bg-surface-alt"}`}
            >
              Open
            </button>
            <button
              data-testid="ar-queue-filter-done"
              onClick={() => setShowDone(true)}
              className={`px-2.5 py-1 font-semibold ${showDone ? "bg-charcoal text-ink-inverse" : "text-ink-secondary hover:bg-surface-alt"}`}
            >
              Done
            </button>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="rounded-md border border-divider-strong px-2 py-1 text-xs text-ink-secondary hover:bg-surface-alt disabled:opacity-50"
            aria-label="Refresh"
            data-testid="ar-queue-refresh"
          >
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        </div>
      </div>

      <ul className="divide-y divide-divider-subtle">
        {loading && (
          <li className="px-5 py-8 text-center text-sm text-ink-muted">Loading…</li>
        )}
        {!loading && tasks.length === 0 && (
          <li className="px-5 py-8 text-center text-sm text-ink-muted">
            {showDone ? "No completed tasks." : "Inbox zero — no Accounts Receivable tasks open."}
          </li>
        )}
        {tasks.map((t) => {
          const fromName = t.ar_from_name || t.ar_from_email || t.customer_name || "";
          const fromEmail = t.ar_from_email || "";
          const receivedAt = t.ar_received_at || t.created_at;
          const snippet = t.ar_snippet || "";
          const hasAttachments = t.ar_has_attachments;
          const isArEmail = t.source_type === "ar_email";
          const isOpen = t.status !== "Completed" && t.status !== "Cancelled";
          return (
            <li
              key={t.id}
              data-testid={`ar-queue-task-${t.id}`}
              className="flex items-start gap-3 px-5 py-3 hover:bg-surface-alt"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                  <span className="font-mono text-[11px] text-ink-muted">{t.task_number}</span>
                  {fromName && (
                    <span className="font-semibold text-charcoal">{fromName}</span>
                  )}
                  {fromEmail && (
                    <span className="font-mono text-xs text-ink-muted">&lt;{fromEmail}&gt;</span>
                  )}
                  <span className="text-xs text-ink-muted">· {fmtTime(receivedAt)}</span>
                  {hasAttachments && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-ink-muted">
                      <Paperclip size={11} weight="bold" /> attachment
                    </span>
                  )}
                </div>
                <button
                  data-testid={`ar-queue-open-${t.id}`}
                  onClick={() => nav(`/tasks?tab=team&task=${t.id}`)}
                  className="mt-0.5 block truncate text-left text-sm font-medium text-ink hover:text-brand hover:underline"
                >
                  {t.subject}
                </button>
                {snippet && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-ink-secondary">{snippet}</div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {isArEmail && (
                  <a
                    data-testid={`ar-queue-download-${t.id}`}
                    href={`${API}/tasks/${t.id}/ar-source-eml`}
                    title="Download original email (.eml)"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-divider-strong bg-surface text-ink-secondary hover:bg-surface-alt"
                  >
                    <DownloadSimple size={14} weight="bold" />
                  </a>
                )}
                {isOpen ? (
                  <button
                    data-testid={`ar-queue-complete-${t.id}`}
                    onClick={() => setStatus(t, "Completed", "Marked complete")}
                    disabled={!!working[t.id]}
                    className="inline-flex h-8 items-center gap-1 rounded-md bg-charcoal px-2.5 text-xs font-semibold text-ink-inverse hover:bg-grey-medium disabled:opacity-50"
                  >
                    <CheckCircle size={13} weight="bold" /> Complete
                  </button>
                ) : (
                  <button
                    data-testid={`ar-queue-reopen-${t.id}`}
                    onClick={() => setStatus(t, "Open", "Reopened")}
                    disabled={!!working[t.id]}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-divider-strong bg-surface px-2.5 text-xs font-semibold text-ink-secondary hover:bg-surface-alt disabled:opacity-50"
                  >
                    <ArrowUUpLeft size={12} weight="bold" /> Reopen
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
